"use client";

import { useEffect } from "react";
import { usePathname, useRouter } from "next/navigation";

import { scheduleAfterLoadIdle } from "@/lib/after-load-idle";

/**
 * Idle-time prefetch of the primary nav targets (instant-storefront Phase B).
 *
 * Nav/footer links are `prefetch={false}` so viewport prefetching can't
 * compete with the LCP image on throttled connections (measured: viewport
 * prefetch pulled ~20 route chunks incl. the Clerk SDK into the load).
 * Hover-prefetch alone leaves a first-tap round-trip on touch devices — so
 * once the browser is idle AFTER the load event, prefetch each nav target's
 * RSC payload. With the ISR mirrors those payloads come from the server
 * cache in ~20ms, making every subsequent menu tap paint instantly from the
 * router cache (staleTimes 120/180s).
 *
 * Renders nothing; mounted once in the sites layout.
 */
export function IdleNavPrefetch({ targets }: { targets: string[] }) {
  const router = useRouter();
  // Re-run per navigation: this island lives in the persistent layout, so a
  // mount-only effect would warm the nav targets ONCE and the router cache
  // would LRU-evict them while browsing (staleTimes 120/180s) — menu taps
  // went cold after a few navigations. Keying the effect to the pathname
  // re-warms them at idle after each navigation.
  const pathname = usePathname();

  useEffect(() => {
    // Only the nav targets: they're `prefetch={false}` (protects LCP from
    // header-link prefetch storms), so nothing else ever warms them. Card
    // links carry NO prefetch prop — Next 16's incremental viewport
    // prefetcher (layout-deduped, cancels off-viewport, hover-prioritized)
    // warms those natively, which made this island's former card-warming
    // pass redundant (verified: dwelled card taps stay ~100ms without it).
    return scheduleAfterLoadIdle(
      () => {
        for (const href of targets) router.prefetch(href);
      },
      { fallbackDelayMs: 3000 },
    );
  }, [router, targets, pathname]);

  return null;
}
